import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Chip,
  IconButton,
  Switch,
  Divider,
  MenuItem,
  Menu,
  ListSubheader,
  ListItemText,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import {
  listSubscriptions,
  createSubscription,
  setSubscriptionEnabled,
  deleteSubscription,
} from '../../api/subscriptions';
import { getTelegramBotChats } from '../../api/userSettings';
import { customEventsApi } from '../../api/customEvents';
import { visualStrategiesApi } from '../../api/visualStrategies';
import {
  INDICATOR_EVENT_DEFINITIONS,
  getEventTypeLabel,
} from '../../constants/eventTypes';
import { extractErrorMessage } from '../../api/extractErrorMessage';

// Sentinel value for the "Custom Event" option in the event-type dropdown.
// Matches the backend's `eventType` on stored custom-event fires and the
// subscription API's `eventTypeId === 'custom_event'` branch — the
// dispatcher's filter requires both eventTypeId and customEventId to match,
// so we ship both fields together when the user picks this option.
const CUSTOM_EVENT_TYPE_ID = 'custom_event';

// Strategy subscriptions come in two flavours — entry-signal alerts and
// exit-signal alerts. They render with the same strategy name, so the list
// chip is colour-coded by direction; the add-form offers both as event
// types (each requiring a strategy) and, for entry only, a mode.
const STRATEGY_ENTRY_EVENT_TYPE = 'strategy.entry.triggered';
const STRATEGY_EXIT_EVENT_TYPE = 'strategy.exit.triggered';
const STRATEGY_EVENT_TYPE_IDS = [STRATEGY_ENTRY_EVENT_TYPE, STRATEGY_EXIT_EVENT_TYPE];

// The opt-in daily briefing digest (one curated "what matters today" Telegram
// message, sent by a scheduled job to subscribers). Unlike indicator events it
// has no timeframe/symbol scope — the digest covers the user's whole watchlist
// — so the add-form hides those pickers and ships wildcards. Matches
// BRIEFING_DIGEST_EVENT_TYPE in serverless-portfolio-tracker's
// briefingDigestService; eventTypeId is free-form on the backend.
const BRIEFING_DIGEST_EVENT_TYPE_ID = 'briefing.digest.daily';

// Entry subs carry a mode — 'confirm' stages a planned trade for review,
// 'auto' opens it directly. Exit subs are pure alerts (no mode). Mirrors
// subscriptionService.SUBSCRIPTION_MODES in serverless-portfolio-tracker.
const SUBSCRIPTION_MODE_CHOICES = [
  { value: 'confirm', label: 'Confirm — stage a planned trade for review' },
  { value: 'auto', label: 'Auto — open the trade directly' },
];
const DEFAULT_SUBSCRIPTION_MODE = 'confirm';

// Canonical short forms match the backend's persisted vocabulary
// (storeEvent normaliser in dwlf-scheduled-jobs writes '1w'/'1d'/'1h'),
// the API enum (MarketEvent schema), and industry convention
// (TradingView, exchanges, MT4/5). Display labels are the human-friendly
// names — "Weekly" reads better than "1W" in a settings dropdown — but
// the form payload sends short forms so the persisted subscription row
// uses the same vocabulary as the events it matches against.
const TIMEFRAME_CHOICES = [
  { value: '1w', label: 'Weekly' },
  { value: '1d', label: 'Daily' },
  { value: '1h', label: 'Hourly' },
];

const TIMEFRAME_LABEL_BY_VALUE = TIMEFRAME_CHOICES.reduce((acc, choice) => {
  acc[choice.value] = choice.label;
  return acc;
}, {});

// Legacy long forms remain readable for any subscription rows that
// pre-date the rename — display them as their human label rather than
// raw "weekly"/"daily"/"hourly", and let the multi-select still hold
// them until the user re-saves. Step 4 migration rewrites these on the
// backend; until then we tolerate them in existing state.
const LEGACY_TIMEFRAME_LABEL = Object.freeze({
  weekly: 'Weekly',
  daily: 'Daily',
  hourly: 'Hourly',
});

const labelForTimeframe = (tf) => (
  TIMEFRAME_LABEL_BY_VALUE[tf] || LEGACY_TIMEFRAME_LABEL[tf] || tf
);

const WILDCARD = '*';

// Default the picker to the cycle events most users will care about. The
// full INDICATOR_EVENT_DEFINITIONS list is large; surface confirmed lows
// and highs first, then offer the rest under "More event types…".
const PRIMARY_EVENT_TYPE_IDS = [
  'cycle.low.confirmed',
  'cycle.low.provisional',
  'cycle.high.confirmed',
  'cycle.high.provisional',
];

const formatChannelSummary = (channels) => {
  if (!Array.isArray(channels) || channels.length === 0) {return '—';}
  return channels
    .map((c) => {
      if (c.type === 'telegram') {
        const id = c.destination?.chatId;
        return id ? `Telegram (chat ${id})` : 'Telegram';
      }
      return c.type;
    })
    .join(', ');
};

const formatList = (values) => {
  if (!Array.isArray(values) || values.length === 0) {return WILDCARD;}
  return values.join(', ');
};

// Display variant for timeframes — uses the human-friendly label map
// so the row reads "Weekly, Daily" rather than "1w, 1d". Wildcard
// passes through.
const formatTimeframeList = (values) => {
  if (!Array.isArray(values) || values.length === 0) {return WILDCARD;}
  if (values.includes(WILDCARD)) {return WILDCARD;}
  return values.map(labelForTimeframe).join(', ');
};

// Render a chat picker entry as a human-readable label. Telegram
// distinguishes private chats (DMs — first/last name + optional
// username) from groups / supergroups / channels (which carry a
// `title` and no name fields). Reflect the distinction so a user can
// tell their own DM apart from a group they added the bot to.
const formatChatLabel = (chat) => {
  if (chat.type === 'private') {
    const name = [chat.firstName, chat.lastName].filter(Boolean).join(' ').trim();
    const handle = chat.username ? `@${chat.username}` : null;
    const display = name || handle || 'Direct message';
    return handle && name ? `${name} (${handle}) — DM` : `${display} — DM`;
  }
  const kindLabel = chat.type === 'channel' ? 'Channel' : 'Group';
  return chat.title ? `${chat.title} — ${kindLabel}` : kindLabel;
};

const SubscriptionsSection = () => {
  const settings = useSelector((state) => state.userSettings.settings);
  const isLinked = Boolean(settings?.telegramBot?.username || settings?.telegramBotTokenConfigured);

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // User's custom events, fetched once on mount so both the add-form
  // dropdown and the existing-subscription list can resolve a
  // customEventId → name without two separate fetches. Empty array on
  // initial render, populated by the effect below; a fetch failure
  // surfaces only in the form (subscription create still works for
  // non-custom event types).
  const [customEvents, setCustomEvents] = useState([]);
  const [customEventsLoading, setCustomEventsLoading] = useState(false);
  const [customEventsError, setCustomEventsError] = useState(null);
  // User's strategies, fetched once on mount to populate the add-form's
  // "Which strategy" picker when the event type is a strategy entry/exit
  // signal. Mirrors the customEvents fetch below.
  const [strategies, setStrategies] = useState([]);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [strategiesError, setStrategiesError] = useState(null);
  // Lookup for the SubscriptionList row display — when a subscription is
  // for eventTypeId 'custom_event', show the user-defined name instead of
  // the generic "Custom Event" label. Built from the list above.
  const customEventNameById = useMemo(() => {
    const map = new Map();
    for (const ev of customEvents) {
      if (ev?.eventId) map.set(ev.eventId, ev.name || ev.eventId);
    }
    return map;
  }, [customEvents]);
  // Set of in-flight subscriptionIds rather than a single scalar — without
  // this, two near-simultaneous toggle/delete clicks on different rows
  // would clobber each other's "in flight" state, prematurely re-enabling
  // the first row's controls and (when the first request finishes) the
  // second row's controls while it's still mid-request. The latter would
  // let a user double-click delete and get a confusing 404.
  const [mutatingIds, setMutatingIds] = useState(() => new Set());

  const beginMutation = (id) => setMutatingIds((prev) => {
    const next = new Set(prev);
    next.add(id);
    return next;
  });

  const endMutation = (id) => setMutatingIds((prev) => {
    if (!prev.has(id)) {return prev;}
    const next = new Set(prev);
    next.delete(id);
    return next;
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listSubscriptions();
      setSubscriptions(list);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load subscriptions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Fetch the user's custom events once on mount. Used by the add-form
  // (second dropdown when "Custom Event" is selected) AND the existing-
  // subscription list (render the event's name rather than "Custom
  // Event" for custom_event rows). Tolerates all three response shapes
  // the API has historically returned:
  //   - `{ events: [...] }`        — customEventsSlice.js:148
  //   - `{ customEvents: [...] }`  — FSMBuilderPage.js:127
  //   - bare array                  — marketDetailPage.js:680 fallback
  // A narrower check (only events + bare array) silently yielded `[]`
  // for callers that get the third shape, making the dropdown read
  // "No custom events found" even when the user had events.
  useEffect(() => {
    let cancelled = false;
    setCustomEventsLoading(true);
    setCustomEventsError(null);
    customEventsApi.listEvents({ limit: 200 })
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response?.events)
          ? response.events
          : (Array.isArray(response?.customEvents)
            ? response.customEvents
            : (Array.isArray(response) ? response : []));
        setCustomEvents(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setCustomEventsError(extractErrorMessage(err, 'Failed to load custom events'));
      })
      .finally(() => {
        if (!cancelled) setCustomEventsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch the user's strategies once on mount for the add-form's "Which
  // strategy" picker. listStrategies returns { strategies: [...] } with
  // strategyId + name on each row.
  useEffect(() => {
    let cancelled = false;
    setStrategiesLoading(true);
    setStrategiesError(null);
    visualStrategiesApi.listStrategies({ limit: 200 })
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response?.strategies)
          ? response.strategies
          : (Array.isArray(response) ? response : []);
        setStrategies(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setStrategiesError(extractErrorMessage(err, 'Failed to load strategies'));
      })
      .finally(() => {
        if (!cancelled) setStrategiesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (subscriptionId, nextEnabled) => {
    beginMutation(subscriptionId);
    setError(null);
    try {
      await setSubscriptionEnabled(subscriptionId, nextEnabled);
      setSubscriptions((prev) => prev.map((s) => (
        s.subscriptionId === subscriptionId ? { ...s, enabled: nextEnabled } : s
      )));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to toggle subscription'));
    } finally {
      endMutation(subscriptionId);
    }
  };

  const handleDelete = async (subscriptionId) => {
    beginMutation(subscriptionId);
    setError(null);
    try {
      await deleteSubscription(subscriptionId);
      setSubscriptions((prev) => prev.filter((s) => s.subscriptionId !== subscriptionId));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete subscription'));
    } finally {
      endMutation(subscriptionId);
    }
  };

  const handleCreated = (created) => {
    setSubscriptions((prev) => [...prev, created]);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Notification Subscriptions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Get pushed when matching events fire. Each subscription names an
          event type and (optionally) narrows by timeframe and symbol, then
          delivers via your Telegram bot.
        </Typography>
      </Box>

      {!isLinked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Link a Telegram bot above before adding subscriptions — without
          one, deliveries will fail.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SubscriptionList
        subscriptions={subscriptions}
        loading={loading}
        mutatingIds={mutatingIds}
        onToggle={handleToggle}
        onDelete={handleDelete}
        customEventNameById={customEventNameById}
      />

      <Divider sx={{ my: 3 }} />

      <AddSubscriptionForm
        disabled={!isLinked}
        onCreated={handleCreated}
        onError={setError}
        customEvents={customEvents}
        customEventsLoading={customEventsLoading}
        customEventsError={customEventsError}
        strategies={strategies}
        strategiesLoading={strategiesLoading}
        strategiesError={strategiesError}
      />
    </Paper>
  );
};

const SubscriptionList = ({ subscriptions, loading, mutatingIds, onToggle, onDelete, customEventNameById }) => {
  if (loading) {
    return <Typography variant="body2" color="text.secondary">Loading subscriptions…</Typography>;
  }
  if (subscriptions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No subscriptions yet. Add one below to start receiving notifications.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5}>
      {subscriptions.map((sub) => (
        <Box
          key={sub.subscriptionId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            opacity: sub.enabled ? 1 : 0.6,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="body2" fontWeight={600}>
                {/*
                  Header resolution order:
                  1. Custom-event subscription → show the event's name
                     (prefer `sub.customEventName` from the API, fall back
                     to the locally-fetched name map for backward compat
                     with the pre-enrichment backend).
                  2. Strategy-bound subscription → show the strategy's
                     name from `sub.strategyName` (added by
                     serverless-portfolio-tracker#449). Multiple strategy
                     subscriptions were previously indistinguishable —
                     all rendered as "Strategy Entry Triggered".
                  3. Otherwise → friendly label for the event type.
                */}
                {sub.eventTypeId === CUSTOM_EVENT_TYPE_ID && sub.customEventId
                  ? (sub.customEventName
                      || customEventNameById?.get(sub.customEventId)
                      || 'Custom Event')
                  : sub.strategyId
                    ? (sub.strategyName || getEventTypeLabel(sub.eventTypeId))
                    : getEventTypeLabel(sub.eventTypeId)}
              </Typography>
              {sub.eventTypeId === CUSTOM_EVENT_TYPE_ID && (
                <Chip size="small" label="Custom" color="primary" variant="outlined" />
              )}
              {sub.strategyId && (
                <Chip
                  size="small"
                  label={sub.eventTypeId === STRATEGY_EXIT_EVENT_TYPE ? 'Strategy · Exit' : 'Strategy · Entry'}
                  color={sub.eventTypeId === STRATEGY_EXIT_EVENT_TYPE ? 'error' : 'success'}
                  variant="outlined"
                />
              )}
              {!sub.enabled && <Chip size="small" label="Disabled" />}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              Timeframes: {formatTimeframeList(sub.timeframes)} · Symbols: {formatList(sub.symbols)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {formatChannelSummary(sub.channels)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={sub.enabled ? 'Disable' : 'Enable'}>
              <Switch
                size="small"
                checked={Boolean(sub.enabled)}
                onChange={(e) => onToggle(sub.subscriptionId, e.target.checked)}
                disabled={mutatingIds.has(sub.subscriptionId)}
              />
            </Tooltip>
            <Tooltip title="Delete subscription">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onDelete(sub.subscriptionId)}
                  disabled={mutatingIds.has(sub.subscriptionId)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const AddSubscriptionForm = ({
  disabled,
  onCreated,
  onError,
  customEvents = [],
  customEventsLoading = false,
  customEventsError = null,
  strategies = [],
  strategiesLoading = false,
  strategiesError = null,
}) => {
  const eventTypeOptions = useMemo(() => {
    const all = INDICATOR_EVENT_DEFINITIONS || [];
    const primarySet = new Set(PRIMARY_EVENT_TYPE_IDS);
    const primary = PRIMARY_EVENT_TYPE_IDS
      .map((id) => all.find((d) => d.id === id))
      .filter(Boolean);
    const others = all.filter((d) => !primarySet.has(d.id));
    return { primary, others };
  }, []);

  const [eventTypeId, setEventTypeId] = useState(PRIMARY_EVENT_TYPE_IDS[0]);
  // When eventTypeId is the custom-event sentinel, the user must also
  // pick which of their custom events to subscribe to. Empty string
  // means "not yet selected" — submit is gated on a real value.
  const [customEventId, setCustomEventId] = useState('');
  const isCustomEventMode = eventTypeId === CUSTOM_EVENT_TYPE_ID;
  // When eventTypeId is a strategy entry/exit signal, the user must also
  // pick which strategy. Empty string = "not yet selected"; submit is gated
  // on a real value. Entry subs additionally carry a mode (confirm/auto);
  // exit subs are pure alerts and send no mode.
  const [strategyId, setStrategyId] = useState('');
  const [mode, setMode] = useState(DEFAULT_SUBSCRIPTION_MODE);
  const isStrategyMode = STRATEGY_EVENT_TYPE_IDS.includes(eventTypeId);
  const isStrategyEntryMode = eventTypeId === STRATEGY_ENTRY_EVENT_TYPE;
  // Briefing digest: no timeframe/symbol scope (covers the whole watchlist), so
  // the form hides those pickers and submits wildcards.
  const isBriefingDigestMode = eventTypeId === BRIEFING_DIGEST_EVENT_TYPE_ID;
  const [timeframes, setTimeframes] = useState(['1w']);
  // Default ON so the friction-free path is "do nothing, get notified for
  // every symbol". Most v1 subscriptions are a single event type fanned
  // out across the user's whole watchlist; narrowing by symbol is the
  // less common case and worth one extra click. Toggling the switch off
  // reveals the comma-separated input — easier than asking users to type
  // a literal asterisk that's tucked behind Shift on most keyboards.
  const [allSymbols, setAllSymbols] = useState(true);
  const [symbolsText, setSymbolsText] = useState('');
  const [chatId, setChatId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // "Find chats" picker state. Lazy-fetched on first click — we don't
  // want to spam Telegram's getUpdates on every form mount, and the
  // picker is opt-in rather than the default flow (manual paste still
  // works for users who already know their id).
  const [chatsMenuAnchor, setChatsMenuAnchor] = useState(null);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chats, setChats] = useState(null); // null = not yet fetched
  const [chatsFetchError, setChatsFetchError] = useState(null);

  // When the toggle is OFF the user has explicitly opted into narrowing
  // by symbol, so blank input is a contradiction (not a hidden shortcut
  // back to wildcard like it was pre-toggle). The submit button uses the
  // emptiness of this list to stay disabled until at least one symbol is
  // listed, surfacing the constraint up-front rather than via a backend
  // 400 or — worse — a sneaky wildcard send.
  const parseSymbols = () => {
    if (allSymbols) {return [WILDCARD];}
    return symbolsText.trim().split(/[\s,]+/).filter(Boolean);
  };

  const parsedSymbols = parseSymbols();
  const symbolsValid = parsedSymbols.length > 0;

  // The multi-select renders [] as '*' to suggest "match all", so the
  // submitted value must match that intent. Sending [] would otherwise
  // be rejected by the backend (which requires a non-empty array of
  // non-empty strings on timeframes when the field is provided) and
  // even if it weren't, the dispatcher's listMatches would treat [] as
  // "no constraint" — same as wildcard but only by accident. Make it
  // explicit instead so the persisted row reflects what the user saw.
  const submittedTimeframes = timeframes.length === 0 ? [WILDCARD] : timeframes;

  const parseChatId = () => {
    const raw = chatId.trim();
    if (!raw) {return null;}
    // Telegram chat ids are integers (often negative for groups). Send
    // as a number when it parses cleanly so the dispatcher's downstream
    // adapter doesn't need string-vs-number reconciliation.
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) && String(asNumber) === raw ? asNumber : raw;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) {return;}
    // Clear any stale error from a prior failed action up-front, mirroring
    // handleToggle / handleDelete in the parent. Without this a previous
    // toggle/delete/create error sticks around in the banner even after
    // this submit succeeds, so the user sees fresh content next to a stale
    // error and can't tell which action it referred to.
    onError?.(null);
    const parsedChatId = parseChatId();
    if (parsedChatId === null) {
      onError?.('Chat id is required');
      return;
    }
    // Custom-event subscriptions require a specific customEventId — the
    // dispatcher's filter rejects subs with mismatched (or missing)
    // customEventId pairings, so an empty value here would mean a
    // subscription that never fires.
    if (isCustomEventMode && !customEventId) {
      onError?.('Pick which custom event to subscribe to');
      return;
    }
    // Strategy subscriptions require a strategyId — the backend rejects a
    // strategy event type without one, so block here for a clean message.
    if (isStrategyMode && !strategyId) {
      onError?.('Pick which strategy to subscribe to');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createSubscription({
        eventTypeId,
        ...(isCustomEventMode ? { customEventId } : {}),
        ...(isStrategyMode ? { strategyId } : {}),
        // Mode is meaningful only for entry subs; exit subs are pure alerts.
        ...(isStrategyEntryMode ? { mode } : {}),
        // The digest has no timeframe/symbol scope — ship wildcards so the row
        // is schema-valid; the digest cron ignores both (it walks the whole
        // watchlist regardless).
        timeframes: isBriefingDigestMode ? [WILDCARD] : submittedTimeframes,
        symbols: isBriefingDigestMode ? [WILDCARD] : parsedSymbols,
        channels: [{ type: 'telegram', destination: { chatId: parsedChatId }, enabled: true }],
      });
      if (created) {
        onCreated?.(created);
        setSymbolsText('');
        setAllSymbols(true);
        setChatId('');
        setCustomEventId('');
        setStrategyId('');
        setMode(DEFAULT_SUBSCRIPTION_MODE);
      }
    } catch (err) {
      onError?.(extractErrorMessage(err, 'Failed to create subscription'));
    } finally {
      setSubmitting(false);
    }
  };

  // "Find chats" — anchors a Menu to the button, lazily fetches the
  // chat list on first open. Re-fetches on subsequent opens so a user
  // who messages their bot mid-flow can refresh without closing the
  // form.
  const handleFindChatsClick = async (e) => {
    setChatsMenuAnchor(e.currentTarget);
    setChatsLoading(true);
    setChatsFetchError(null);
    try {
      const list = await getTelegramBotChats();
      setChats(list);
    } catch (err) {
      // Distinguish the two precondition failures (422s with specific
      // codes) from generic errors so the Menu can guide the user
      // rather than dump a raw message. Both come back with the
      // standard error envelope { code, message, status } from the
      // backend; extractErrorMessage surfaces the message verbatim.
      const code = err?.response?.data?.error?.code;
      if (code === 'TELEGRAM_WEBHOOK_CONFLICT') {
        setChatsFetchError('Your bot has a webhook configured, which Telegram doesn\'t allow alongside this lookup. Delete the webhook in BotFather (or via setWebhook with an empty URL) and try again.');
      } else if (code === 'TELEGRAM_INVALID_TOKEN') {
        setChatsFetchError('Your bot token has been revoked. Unlink and re-link the bot above.');
      } else {
        setChatsFetchError(extractErrorMessage(err, 'Failed to fetch chats from Telegram'));
      }
    } finally {
      setChatsLoading(false);
    }
  };

  const handleSelectChat = (chat) => {
    setChatId(String(chat.id));
    setChatsMenuAnchor(null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Add subscription
      </Typography>
      <Stack spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel id="sub-event-type-label">Event type</InputLabel>
          <Select
            labelId="sub-event-type-label"
            label="Event type"
            value={eventTypeId}
            onChange={(e) => {
              const next = e.target.value;
              setEventTypeId(next);
              // Reset the dependent pickers on any switch — otherwise a
              // stale customEventId / strategyId hangs around in state and
              // would ride along if the user toggled back to that mode.
              if (next !== CUSTOM_EVENT_TYPE_ID) setCustomEventId('');
              if (!STRATEGY_EVENT_TYPE_IDS.includes(next)) setStrategyId('');
              // Mode only applies to entry subs. Reset it to the safe
              // default (Confirm) whenever we leave entry, so a prior "Auto"
              // choice can't silently ride back when the user returns to
              // entry — Auto opens trades directly, no review.
              if (next !== STRATEGY_ENTRY_EVENT_TYPE) setMode(DEFAULT_SUBSCRIPTION_MODE);
            }}
            disabled={disabled || submitting}
          >
            {eventTypeOptions.primary.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
            <Divider key="strategy-divider" />
            <ListSubheader key="strategy-header">Strategy signals</ListSubheader>
            <MenuItem key={STRATEGY_ENTRY_EVENT_TYPE} value={STRATEGY_ENTRY_EVENT_TYPE}>
              Strategy entry signal…
            </MenuItem>
            <MenuItem key={STRATEGY_EXIT_EVENT_TYPE} value={STRATEGY_EXIT_EVENT_TYPE}>
              Strategy exit signal, stop-outs and stop moves…
            </MenuItem>
            <Divider key="custom-divider" />
            <ListSubheader key="custom-header">Custom events</ListSubheader>
            <MenuItem key={CUSTOM_EVENT_TYPE_ID} value={CUSTOM_EVENT_TYPE_ID}>
              Custom event…
            </MenuItem>
            <Divider key="briefing-divider" />
            <ListSubheader key="briefing-header">Briefing</ListSubheader>
            <MenuItem key={BRIEFING_DIGEST_EVENT_TYPE_ID} value={BRIEFING_DIGEST_EVENT_TYPE_ID}>
              Daily briefing digest
            </MenuItem>
            {eventTypeOptions.others.length > 0 && [
              <Divider key="others-divider" />,
              <ListSubheader key="others-header">Other indicator events</ListSubheader>,
              ...eventTypeOptions.others.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              )),
            ]}
          </Select>
        </FormControl>

        {isCustomEventMode && (
          <FormControl size="small" fullWidth>
            <InputLabel id="sub-custom-event-label">Which custom event</InputLabel>
            <Select
              labelId="sub-custom-event-label"
              label="Which custom event"
              value={customEventId}
              onChange={(e) => setCustomEventId(e.target.value)}
              disabled={disabled || submitting || customEventsLoading}
            >
              {customEventsLoading && (
                <MenuItem disabled value="">Loading custom events…</MenuItem>
              )}
              {/* Distinguish "fetch failed" from "genuinely empty" — both
                  produce customEvents.length === 0, but the user-facing
                  remedy is different. The "create one first" hint is
                  actively misleading when there are events but the fetch
                  errored. The detailed error text still renders below
                  the Select. */}
              {!customEventsLoading && customEvents.length === 0 && customEventsError && (
                <MenuItem disabled value="">
                  Couldn't load custom events — see error below
                </MenuItem>
              )}
              {!customEventsLoading && customEvents.length === 0 && !customEventsError && (
                <MenuItem disabled value="">
                  No custom events found — create one first in the Events page
                </MenuItem>
              )}
              {!customEventsLoading && customEvents.map((ev) => (
                <MenuItem key={ev.eventId} value={ev.eventId}>
                  {ev.name || ev.eventId}
                </MenuItem>
              ))}
            </Select>
            {customEventsError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {customEventsError}
              </Typography>
            )}
          </FormControl>
        )}

        {isStrategyMode && (
          <FormControl size="small" fullWidth>
            <InputLabel id="sub-strategy-label">Which strategy</InputLabel>
            <Select
              labelId="sub-strategy-label"
              label="Which strategy"
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
              disabled={disabled || submitting || strategiesLoading}
            >
              {strategiesLoading && (
                <MenuItem disabled value="">Loading strategies…</MenuItem>
              )}
              {/* Same distinction as custom events: "fetch failed" vs
                  "genuinely empty" need different remedies. */}
              {!strategiesLoading && strategies.length === 0 && strategiesError && (
                <MenuItem disabled value="">
                  Couldn't load strategies — see error below
                </MenuItem>
              )}
              {!strategiesLoading && strategies.length === 0 && !strategiesError && (
                <MenuItem disabled value="">
                  No strategies found — create one first in the Strategies page
                </MenuItem>
              )}
              {!strategiesLoading && strategies.map((s) => (
                <MenuItem key={s.strategyId} value={s.strategyId}>
                  {s.name || s.strategyId}
                </MenuItem>
              ))}
            </Select>
            {strategiesError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {strategiesError}
              </Typography>
            )}
          </FormControl>
        )}

        {isStrategyEntryMode && (
          <FormControl size="small" fullWidth>
            <InputLabel id="sub-mode-label">Mode</InputLabel>
            <Select
              labelId="sub-mode-label"
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={disabled || submitting}
            >
              {SUBSCRIPTION_MODE_CHOICES.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isBriefingDigestMode && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel id="sub-timeframes-label">Timeframes</InputLabel>
              <Select
                labelId="sub-timeframes-label"
                label="Timeframes"
                multiple
                value={timeframes}
                onChange={(e) => {
                  const value = e.target.value;
                  setTimeframes(typeof value === 'string' ? value.split(',') : value);
                }}
                disabled={disabled || submitting}
                renderValue={(selected) => (
                  selected.length ? selected.map(labelForTimeframe).join(', ') : WILDCARD
                )}
              >
                {TIMEFRAME_CHOICES.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={allSymbols}
                    onChange={(e) => setAllSymbols(e.target.checked)}
                    disabled={disabled || submitting}
                  />
                )}
                label="All symbols"
              />
              {!allSymbols && (
                <TextField
                  size="small"
                  fullWidth
                  label="Symbols (comma-separated)"
                  placeholder="BTC-USD, ETH-USD"
                  value={symbolsText}
                  onChange={(e) => setSymbolsText(e.target.value)}
                  disabled={disabled || submitting}
                  helperText="Add at least one symbol, or flip the toggle on for all"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </>
        )}

        {isBriefingDigestMode && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            One curated daily summary of what matters across your whole watchlist —
            sent each morning after the daily data refresh.
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            required
            label="Telegram chat id"
            placeholder="123456789"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            disabled={disabled || submitting}
            helperText="Send any message to your bot in Telegram, then click Find chats — or paste the id here directly."
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleFindChatsClick}
            // Disable while a fetch is in-flight so a user dismissing the
            // menu (Esc) and re-clicking can't race two getUpdates calls.
            // The earlier call's `finally` would otherwise set
            // chatsLoading=false while the second is still pending,
            // flashing the spinner off and showing stale results.
            disabled={disabled || submitting || chatsLoading}
            // Match TextField height (which is the small variant) so the
            // button doesn't bob below it. mt aligns with the input's
            // baseline above the helper text.
            sx={{ flexShrink: 0, mt: '4px', height: 40 }}
          >
            Find chats
          </Button>
        </Stack>

        <Menu
          anchorEl={chatsMenuAnchor}
          open={Boolean(chatsMenuAnchor)}
          onClose={() => setChatsMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 320, maxWidth: 480 } } }}
        >
          <ListSubheader sx={{ lineHeight: 1.4, py: 1 }}>
            Recent chats with your bot
          </ListSubheader>
          {chatsLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!chatsLoading && chatsFetchError && (
            <Box px={2} py={1.5}>
              <Typography variant="caption" color="error">
                {chatsFetchError}
              </Typography>
            </Box>
          )}
          {!chatsLoading && !chatsFetchError && chats !== null && chats.length === 0 && (
            <Box px={2} py={1.5}>
              <Typography variant="caption" color="text.secondary">
                No recent chats. Open Telegram, send any message (e.g. /start) to your bot, then click Find chats again.
              </Typography>
            </Box>
          )}
          {!chatsLoading && !chatsFetchError && chats?.map((chat) => (
            <MenuItem key={chat.id} onClick={() => handleSelectChat(chat)}>
              <ListItemText
                primary={formatChatLabel(chat)}
                secondary={`id ${chat.id}`}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          ))}
        </Menu>

        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disabled={disabled || submitting || !chatId.trim() || (!isBriefingDigestMode && !symbolsValid) || (isCustomEventMode && !customEventId) || (isStrategyMode && !strategyId)}
          >
            {submitting ? 'Adding…' : 'Add subscription'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default SubscriptionsSection;
